// public/v3/src/lib/careplan-generate.js — single-patient AI care plan
// generation + persistence, ported verbatim from v2's bulk-careplan loop.
//
// Sources (v2 line numbers in public/ccm-compliance.js):
//   - bcpGenerate per-patient body  → 14483-14540 (prompt-build + callAI + save)
//   - saveCarePlan persistence shape → 5734-5755
//   - buildFullCarePlanPrompt        → 14572-14589
//   - buildMaintenanceCarePlanPrompt → 14560-14570
//   - bcpBuildPatientDataBlock       → 12970-13001
//   - buildCustomizedPrompt          → 5491-5506
//   - bcpInterpolate                 → 12961-12966
//   - bcpGetTpl                      → 12894-12900
//   - bcpFetchLatestVisitNotes       → 13161-13177
//   - bcpPriorCarePlanDate           → 13178-13184
//   - bcpBuildLabTrends              → 13191-13230
//   - bcpNoteWithinThroughDate       → 13185-13190
//   - reidentifyText                 → 3623-3643
//   - callAI                         → 3525-3590
//   - normalizeAiModel               → 3409-3416
//   - monthLastDayString             → 2168-2175
//
// v2 → v3 adaptations (allowed-change list only):
//   - getPatient(id) → patient passed in by caller
//   - _fetchWithTimeout(API_BASE+url, opts) → ccmApi wrappers / api.post
//   - dbPost('/data/careplans/...', { plan_text }) → ccmApi.saveCarePlan
//   - _cpHistory cache → not maintained client-side in v3 (Studio refetches
//     via invalidateDraftCache())
//   - DOM reads (progress bar, AI loading) → omitted; the caller drives UX
//   - localStorage 'ccm_ai_settings' read → settings.aiModel from ccmApi.getSettings()
//   - practiceSettings global → not used (v2 careplan prompts don't reference it)

import { api, ccmApi } from './api';
import { invalidateDraftCache } from './draft';
import {
  BCP_STANDARD_DEFAULT,
  BCP_MAINTENANCE_DEFAULT,
  BCP_COMPLEX_DEFAULT,
  BCP_PCM_DEFAULT,
} from './v2-prompts';
import { isPcmProgramCode, pcmConditionText, pcmPrincipalCondition } from './pcm-principal';
import { dedupeMedicationsByDrug } from './medication-dedupe';
import { buildBhiPromptSection } from './careplan-bhi';
import { buildPainPromptSection } from './careplan-pain';
// Chart free text is tag-neutralized at every prompt interpolation site so
// a note/condition/plan can never impersonate prompt structure; the Block
// variant preserves line structure and clinical "<" comparisons (see
// prompt-text.js — generalizes the pain-section fix, adversarial review
// 2026-07-21).
import { promptSafeLine, promptSafeBlock } from './prompt-text';
import { buildUserMessages } from './ai-messages.cjs';
import { buildMaterializedNote } from './batch-materialize.cjs';

// ── Template cache loaded from /data/settings (v2 keys preserved) ────────────
// v2 source: _bcpTplCache + bcpLoadTplsFromSettings (ccm-compliance.js:12893, 12919-12928)
const _bcpTplCache = { standard: null, maintenance: null, complex: null, pcm: null };
let _tplLoaded = false;
let _tplInflight = null;

async function loadTemplates() {
  if (_tplLoaded) return;
  if (_tplInflight) return _tplInflight;
  _tplInflight = (async () => {
    try {
      const s = await ccmApi.getSettings();
      if (s) {
        if (s.bulk_cp_standard_template != null) _bcpTplCache.standard = s.bulk_cp_standard_template || null;
        if (s.bulk_cp_maintenance_template != null) _bcpTplCache.maintenance = s.bulk_cp_maintenance_template || null;
        if (s.bulk_cp_complex_template != null) _bcpTplCache.complex = s.bulk_cp_complex_template || null;
        if (s.bulk_cp_pcm_template != null) _bcpTplCache.pcm = s.bulk_cp_pcm_template || null;
      }
    } catch (_) { /* fall back to defaults */ }
    _tplLoaded = true;
    _tplInflight = null;
  })();
  return _tplInflight;
}

// v2 source: bcpGetTpl (ccm-compliance.js:12894-12900)
function bcpGetTpl(name) {
  const v = _bcpTplCache[name];
  if (v != null && String(v).trim()) return v;
  if (name === 'maintenance') return BCP_MAINTENANCE_DEFAULT;
  if (name === 'complex') return BCP_COMPLEX_DEFAULT;
  if (name === 'pcm') return BCP_PCM_DEFAULT;
  return BCP_STANDARD_DEFAULT;
}

// v2 source: bcpInterpolate (ccm-compliance.js:12961-12966)
function bcpInterpolate(template, vars) {
  if (!template) return '';
  return template.replace(/\{\{(\w+)\}\}/g, function(_, key) {
    return vars && vars[key] != null ? String(vars[key]) : '';
  });
}

function bcpOutsideProvidersToString(providers) {
  if (typeof providers === 'string') return promptSafeLine(providers);
  return (Array.isArray(providers) ? providers : [])
    .map(function(op) {
      if (typeof op === 'string') return promptSafeLine(op, 160);
      return [promptSafeLine(op.name, 80), promptSafeLine(op.specialty, 80)].filter(Boolean).join(', ');
    })
    .filter(Boolean)
    .join('; ');
}

function bcpProviderTeamToString(team) {
  const members = Array.isArray(team && team.teamMembers) ? team.teamMembers
    : (Array.isArray(team && team.team_members) ? team.team_members : []);
  if (!members.length) return '';
  return members.slice(0, 8).map(function(m) {
    const name = promptSafeLine(m.name || m.providerName || m.physician_name || '', 80);
    const specialty = promptSafeLine(m.specialty || m.providerSpecialty || '', 80);
    const label = name
      ? (specialty ? name + ' (' + specialty + ')' : name)
      : (specialty ? specialty + ' provider team member' : 'listed provider team member');
    const details = [];
    if (m.group) details.push('group: ' + promptSafeLine(m.group, 80));
    if (m.rank != null) details.push('rank: ' + promptSafeLine(m.rank, 20));
    if (m.treatmentReason) details.push('reason: ' + promptSafeLine(m.treatmentReason, 160));
    if (m.latestActivity) details.push('latest activity: ' + String(m.latestActivity).slice(0, 10));
    return details.length ? label + ' [' + details.join('; ') + ']' : label;
  }).filter(Boolean).join('; ');
}

function bcpPhoneNotesToString(notes) {
  const rows = (Array.isArray(notes) ? notes : [])
    .filter(function(n) { return n && (n.text || n.date || n.document_date); })
    .sort(function(a, b) {
      return String(a.date || a.document_date || '').localeCompare(String(b.date || b.document_date || ''));
    })
    .slice(0, 6)
    .map(function(n) {
      const date = String(n.date || n.document_date || 'undated').slice(0, 10);
      const text = promptSafeLine(String(n.text || '').replace(/\s+/g, ' '), 500);
      return '- ' + date + ': ' + (text || 'phone/non-visit note text not supplied');
    });
  return rows.join('\n');
}

// v2 source: bcpBuildPatientDataBlock (ccm-compliance.js:12970-13001)
function bcpBuildPatientDataBlock(patient, ctx) {
  const p = patient;
  const conditions = (p.conditions || [])
    .map(function(c){ return promptSafeLine(typeof c === 'string' ? c : (c.description || c.conditionId || ''), 160); })
    .filter(Boolean).join(', ') || 'Not specified';
  // Deduplicate medications by drug identity (drug name with strength
  // stripped) — keep only the entry with the most recent start_date when
  // the same drug appears at multiple strengths. Avoids the AI listing
  // "Eliquis 2.5 mg AND Eliquis 5 mg" when the patient is actually on the
  // most recently prescribed strength only. See medication-dedupe.js for
  // tiebreaker rules when start_date is missing.
  const dedupedMeds = dedupeMedicationsByDrug(p.medications);
  // Absence labels, not clinical claims: 'None listed' taught the model to
  // write "patient takes no medications"; 'NKDA' fabricated an allergy status
  // from missing data (compliance review 2026-07-07). Elation sync always
  // supplies the medications ARRAY on a successful sync, so a present-but-empty
  // array is an affirmative zero-active-meds record; a missing/non-array field
  // means the data was never supplied to the generator.
  const medNames = dedupedMeds
    .map(function(m){ return promptSafeLine(typeof m === 'string' ? m : (m.name || ''), 160); })
    .filter(Boolean).join(', ');
  const meds = medNames
    || (Array.isArray(p.medications)
      ? 'Record affirmatively documents zero active medications (synced medication list is empty)'
      : 'No medication data supplied — do NOT state the patient takes no medications');
  const allergies = (Array.isArray(p.allergies) ? p.allergies : [])
    .map(function(a){ return promptSafeLine(typeof a === 'string' ? a : (a.name || ''), 120); })
    .filter(Boolean).join(', ')
    || 'No allergy data supplied — do NOT state an allergy status (e.g., NKDA)';
  const localOutsideProviders = bcpOutsideProvidersToString(p.outsideProviders);
  const providerTeam = bcpProviderTeamToString(ctx && ctx.providerTeam);
  const outsideProviderSources = [];
  if (localOutsideProviders) outsideProviderSources.push('Local Outside Providers: ' + localOutsideProviders);
  if (providerTeam) outsideProviderSources.push('Elation Patient Provider Team: ' + providerTeam);
  const sexLabel = p.sex === 'F' ? 'Female' : p.sex === 'M' ? 'Male' : '';
  const lines = [];
  if (sexLabel) lines.push('- Sex at birth: ' + sexLabel);
  lines.push('- Conditions: ' + conditions);
  lines.push('- Medications: ' + meds);
  lines.push('- Allergies: ' + allergies);
  lines.push('- Outside Specialists / Care Team: ' + (
    outsideProviderSources.length
      ? outsideProviderSources.join(' | ')
      : 'None listed in supplied outside-provider or Elation provider-team data'
  ));
  let sinceLabel;
  if (!ctx || !ctx.sinceDate) sinceLabel = ' (most recent)';
  else if (ctx.labStale) sinceLabel = ' (most recent available — no new labs since ' + ctx.sinceDate + ')';
  else sinceLabel = ' (since ' + ctx.sinceDate + ')';
  // When every lab in this block predates the start of the current reporting
  // month, override the header with a HISTORICAL BASELINE marker. The
  // grounding tail in buildCustomizedPrompt instructs the AI to cite values
  // from a HISTORICAL-flagged section with their date as baseline (not as
  // current measurements). Without this, the AI was treating months-old labs
  // as current findings in the Problem List prognosis.
  if (ctx && ctx.labAllPreMonth && ctx.monthStartDate) {
    sinceLabel = ' — HISTORICAL BASELINE (no labs in the current reporting period '
      + ctx.monthStartDate.slice(0, 7)
      + '; values below predate the reporting period and must be cited with their date as historical context, NOT as current findings)';
  }
  if (ctx && ctx.labTrends) lines.push('\nLAB TRENDS' + sinceLabel + ':\n' + promptSafeBlock(ctx.labTrends));
  if (ctx && ctx.latestNote && ctx.latestNote.text) {
    const noteDate = ctx.latestNote.document_date || '';
    const noteHistorical = !!(ctx.monthStartDate && noteDate && String(noteDate).slice(0, 10) < ctx.monthStartDate);
    const dateLabel = noteDate ? ' (' + noteDate + ')' : '';
    const historicalSuffix = noteHistorical
      ? ' — HISTORICAL (this encounter predates the current reporting period '
        + ctx.monthStartDate.slice(0, 7)
        + '; reference for clinical context only, NOT as the current encounter)'
      : '';
    lines.push('\nMOST RECENT OFFICE VISIT NOTE' + dateLabel + historicalSuffix + ':\n' + promptSafeBlock(ctx.latestNote.text));
  }
  if (ctx && ctx.monthStr) {
    const phoneNotes = bcpPhoneNotesToString(ctx.phoneNotes);
    lines.push('\nPHONE/NON-VISIT CARE COORDINATION NOTES (' + ctx.monthStr + '):\n'
      + (phoneNotes || 'No phone/non-visit notes supplied for this month — omit this topic; do NOT state that no contact occurred.'));
  }
  // Prior plan is injected by buildCustomizedPrompt as a separate {{priorPlan}}
  // block with explicit "revise this" framing — do not duplicate it here.
  return lines.join('\n');
}

// Prior-plan revise block. Shared by buildCustomizedPrompt and
// buildCustomizedPromptCached so the instruction text can never drift between
// the cached and non-cached paths (a drift would change what the model sees).
function _priorPlanBlock(p) {
  if (!(p && p.priorPlan && p.priorPlan.text)) return '';
  const isComplex = p.tierCode === '99487';
  // 99487 requires establishment or substantial revision per service period;
  // 99490/99491 allow natural evolution. The trailing groundingTail is
  // identical across tiers — prevents the AI from carrying forward prior-
  // plan numeric values as if they were current measurements (real risk
  // surfaced during smoke testing).
  const groundingTail = ' Numeric values from the prior plan (lab results, vital signs, BP readings, eGFR, weight, etc.) are historical, not current. A value may be cited as the patient\'s current measurement only if it appears in the LAB TRENDS or MOST RECENT OFFICE VISIT NOTE sections of the current patient data above AND those sections are NOT flagged as HISTORICAL BASELINE / predating the current reporting period in their header. When a section header indicates the data is HISTORICAL (e.g., labs from a prior reporting period, or a visit note predating the current month), cite values with their date as historical context (e.g., "last documented hemoglobin was 8.4 g/dL on December 5, 2025; repeat lab panel ordered"), NEVER as current findings, current measurements, or as the anchor for the patient\'s current prognosis or treatment goals. Values from the prior plan may be referenced only as previously documented (e.g., "last documented HbA1c was 8.2 in March").';
  const reviseInstr = (isComplex
    ? 'The following is the prior care plan for this patient. Substantially revise it for this billing period based on the current patient data above — 99487 (Complex CCM) requires establishment OR substantial revision per service period, and a maintenance-only carry-forward does not meet 99487 criteria. Identify what has changed clinically and update goals, monitoring intervals, medication targets, interventions, and care coordination accordingly. Refresh any dates, lab values, and "this month" references in the prior text so the revised plan reflects the current reporting period. Keep the same bullet-point structure.'
    : p.pcmScoped
    ? 'The following is the prior care plan for this patient. Revise it based on the current patient data above, keeping the plan scoped to the PCM principal condition per the pcm_scope_rule — if the prior plan contains content about other conditions, drop that content rather than carrying it forward. Carry forward principal-condition items that remain accurate; update what has changed. Refresh any dates, lab values, and "this month" references in the prior text so the revised plan reflects the current reporting period. Keep the same bullet-point structure.'
    : 'The following is the prior care plan for this patient. Revise it based on the current patient data above. Carry forward items that remain accurate. Update what has changed — new conditions, revised goals, medication changes, new interventions, changed providers. Refresh any dates, lab values, and "this month" references in the prior text so the revised plan reflects the current reporting period. Keep the same bullet-point structure. The revised plan should be a natural evolution, not a rewrite.'
  ) + groundingTail;
  return '\n\n---\n\nPRIOR MONTH CARE PLAN (' + (p.priorPlan.month || '') + '):\n'
    + reviseInstr + '\n\n'
    + promptSafeBlock(p.priorPlan.text);
}

// Template routing: complex (99487) > pcm (99424/99426 with an UNAMBIGUOUS
// principal condition — p.pcmScoped is set by the buildFullCarePlanPrompt*
// callers from lib/pcm-principal) > standard. A PCM tier whose principal
// condition can't be resolved falls back to the standard template: the
// billing-note gate already blocks the PCM bill for that month, so the chart
// gets a valid multi-chronic plan instead of a mis-scoped disease plan.
function _bcpTplNameFor(p) {
  if (p.tierCode === '99487') return 'complex';
  if (isPcmProgramCode(p.tierCode) && p.pcmScoped) return 'pcm';
  return 'standard';
}

// v2 source: buildCustomizedPrompt (ccm-compliance.js:5491-5506)
function buildCustomizedPrompt(patientData) {
  const p = patientData || {};
  const tplName = _bcpTplNameFor(p);
  return bcpInterpolate(bcpGetTpl(tplName), {
    patientData: p.patientDataBlock || '',
    priorPlan: _priorPlanBlock(p),
  });
}

// Caching variant: split the rendered prompt into a stable {cachedPrefix,
// dynamicSuffix} pair at the {{patientData}} marker. The prefix (template head:
// role + rules) is byte-identical for every patient on a tier, so Bedrock can
// cache it across requests in a bulk run (~10% input cost on a cache read).
// Reconstruction invariant: cachedPrefix + dynamicSuffix === buildCustomizedPrompt(input).
// Safety: if the template lacks the marker, or the head contains its own
// {{placeholder}} (which would be wrong to send raw / unstable to cache), fall
// back to the non-cached single string (cachedPrefix '').
function buildCustomizedPromptCached(patientData) {
  const p = patientData || {};
  const priorPlanBlock = _priorPlanBlock(p);
  const tplName = _bcpTplNameFor(p);
  const tpl = bcpGetTpl(tplName);
  const vars = { patientData: p.patientDataBlock || '', priorPlan: priorPlanBlock };
  const marker = '{{patientData}}';
  const idx = tpl.indexOf(marker);
  const cachedPrefix = idx >= 0 ? tpl.slice(0, idx) : '';
  if (idx < 0 || /\{\{\w+\}\}/.test(cachedPrefix)) {
    return { cachedPrefix: '', dynamicSuffix: bcpInterpolate(tpl, vars) };
  }
  // Interpolate ONLY the template tail, then prepend the patient data verbatim
  // (codex review 2026-07-20): running bcpInterpolate over the concatenation
  // would scan PATIENT-CONTROLLED text for {{placeholders}} — a chart field
  // containing literal "{{priorPlan}}" would be expanded (or an unknown
  // {{word}} deleted) on the cached path only, diverging from the non-cached
  // builder, whose single replace pass never rescans replacement content.
  const dynamicSuffix = (p.patientDataBlock || '') + bcpInterpolate(tpl.slice(idx + marker.length), vars);
  return { cachedPrefix, dynamicSuffix };
}

// v2 source: buildMaintenanceCarePlanPrompt (ccm-compliance.js:14560-14570)
function buildMaintenanceCarePlanPrompt(patient, priorPlanText, sinceDate, ctx) {
  const p = patient;
  const conditions = (p.conditions || [])
    .map(function(c){ return promptSafeLine(typeof c === 'string' ? c : (c.description || c.conditionId || ''), 160); })
    .filter(Boolean).join(', ') || 'Not specified';
  let prompt = bcpInterpolate(bcpGetTpl('maintenance'), {
    since_date: sinceDate || '',
    conditions: conditions,
    prior_plan: promptSafeBlock(priorPlanText || ''),
  });
  const c = ctx || {};
  if (c.monthStr || c.providerTeam || c.phoneNotes) {
    const contextBlock = bcpBuildPatientDataBlock(p, {
      monthStr: c.monthStr,
      providerTeam: c.providerTeam,
      phoneNotes: c.phoneNotes,
    });
    prompt += '\n\n<current_care_coordination_source_data>\n'
      + contextBlock
      + '\n</current_care_coordination_source_data>\n'
      + 'Use the current care coordination source data only to update documented specialist/team membership or documented phone/non-visit care coordination. Do not create new completion attestations, medication reconciliation claims, active coordination claims, referrals, or care transitions unless explicitly documented in that source data or the prior plan.';
  }
  return prompt;
}

// Whether a no-new-data month may carry the prior plan forward (maintenance
// prompt) instead of regenerating the full template. Complex CCM (99487) is
// EXCLUDED: the maintenance template affirmatively documents the patient as
// "continues on the previously documented plan unchanged", which gratuitously
// undercuts a 99487 claim — HHS-OIG (A-07-19-05122) contrasts the nature of
// care planning between complex ("establishment or substantial revision") and
// non-complex CCM. Forcing the full Complex template documents active management
// of interacting conditions without needing new chart data, so the chart stops
// arguing against the bill. Non-complex tiers (99490/99491) may still maintain.
function shouldUseMaintenancePrompt(patient, noNewData, priorPlanText) {
  if (!noNewData || !priorPlanText) return false;
  const program = patient && patient.billing && patient.billing.program;
  // PCM (99424/99426) is also excluded (codex review 2026-07-20): the generic
  // maintenance template affirms an unchanged carry-forward with no PCM scope
  // rule, so a prior multi-chronic plan would be preserved as the plan
  // supporting a disease-specific PCM month. The full PCM template's scoped
  // prior-plan instruction handles no-new-data months instead.
  return program !== '99487' && !isPcmProgramCode(program);
}

// v2 source: buildFullCarePlanPrompt (ccm-compliance.js:14572-14589)
// Exported for prompt-eval harnesses/tests (pure — no fetch, no persistence).
// PCM generation gate (codex review 2026-07-20): a PCM month must NEVER save
// a multi-chronic plan — an unresolved principal condition fails generation
// BEFORE any AI call or save, instead of quietly falling back to the standard
// template (the billing-note gate blocks the bill, but it would not undo a
// saved wrong-scope plan, and later assigning the condition would then bill
// against it). Enrollment persists billing.pcmCondition, so this only fires
// on legacy enrollments or after an EHR sync renamed the designated disease.
function _pcmPlanGate(p) {
  if (!isPcmProgramCode(p && p.billing && p.billing.program)) return;
  if (!pcmPrincipalCondition(p)) {
    throw new Error('PCM care plan blocked: principal condition unresolved — re-select it via PCM enrollment (billing.pcmCondition) before generating.');
  }
}

// PCM threading shared by both prompt entry points: when the tier is PCM and
// the principal condition resolves (lib/pcm-principal), append the scope
// anchor to the patient data block (dynamic portion — keeps the template
// prefix byte-stable for Bedrock prompt caching) and mark the input pcmScoped
// so _bcpTplNameFor selects the disease-specific template.
function _pcmPromptInput(patient, tierCode, patientDataBlock) {
  if (!isPcmProgramCode(tierCode)) return { patientDataBlock, pcmScoped: false };
  const principal = pcmPrincipalCondition(patient);
  if (!principal) return { patientDataBlock, pcmScoped: false };
  const desc = promptSafeLine(pcmConditionText(principal), 200);
  if (!desc) return { patientDataBlock, pcmScoped: false };
  return {
    pcmScoped: true,
    patientDataBlock: patientDataBlock
      + '\n\nPCM PRINCIPAL CONDITION (this disease-specific plan is scoped to this single condition): '
      + desc,
  };
}

export function buildFullCarePlanPrompt(patient, ctx) {
  const c = ctx || {};
  const tierCode = c.tierCode || (patient && patient.billing && patient.billing.program) || '';
  const pcm = _pcmPromptInput(patient, tierCode, bcpBuildPatientDataBlock(patient, c));
  const priorPlan = c.priorPlanText
    ? { month: c.priorPlanMonth || '', text: c.priorPlanText }
    : null;
  return buildCustomizedPrompt({ patientDataBlock: pcm.patientDataBlock, priorPlan, tierCode, pcmScoped: pcm.pcmScoped });
}

// Caching variant of buildFullCarePlanPrompt — returns { cachedPrefix,
// dynamicSuffix } instead of a single string. Used by the generation path so
// the static template prefix caches across a bulk run.
function buildFullCarePlanPromptCached(patient, ctx) {
  const c = ctx || {};
  const tierCode = c.tierCode || (patient && patient.billing && patient.billing.program) || '';
  const pcm = _pcmPromptInput(patient, tierCode, bcpBuildPatientDataBlock(patient, c));
  const priorPlan = c.priorPlanText
    ? { month: c.priorPlanMonth || '', text: c.priorPlanText }
    : null;
  return buildCustomizedPromptCached({ patientDataBlock: pcm.patientDataBlock, priorPlan, tierCode, pcmScoped: pcm.pcmScoped });
}

// v2 source: bcpFetchLatestVisitNotes (ccm-compliance.js:13161-13177)
async function bcpFetchLatestVisitNotes(selected) {
  const byEid = {};
  const ids = selected.map(function(p) { return p.elation_id ? String(p.elation_id) : null; }).filter(Boolean);
  if (!ids.length) return byEid;
  try {
    const data = await ccmApi.latestVisitNotes(ids);
    const results = (data && data.results) || {};
    Object.keys(results).forEach(function(k) { if (results[k]) byEid[k] = results[k]; });
  } catch (e) {}
  return byEid;
}

async function bcpFetchPatientProviderTeams(selected) {
  const byEid = {};
  const ids = selected.map(function(p) { return p.elation_id ? String(p.elation_id) : null; }).filter(Boolean);
  if (!ids.length) return byEid;
  try {
    const data = await ccmApi.patientProviderTeams(ids);
    const results = (data && data.results) || {};
    Object.keys(results).forEach(function(k) { if (results[k]) byEid[k] = results[k]; });
  } catch (e) {}
  return byEid;
}

async function bcpFetchPhoneNotesInMonth(selected, monthStr) {
  const byEid = {};
  const ids = selected.map(function(p) { return p.elation_id ? String(p.elation_id) : null; }).filter(Boolean);
  if (!ids.length || !monthStr) return byEid;
  try {
    const data = await ccmApi.phoneNotesInMonth(ids, monthStr);
    const results = (data && data.results) || {};
    Object.keys(results).forEach(function(k) { byEid[k] = Array.isArray(results[k]) ? results[k] : []; });
  } catch (e) {}
  return byEid;
}

// v2 source: bcpNoteWithinThroughDate (ccm-compliance.js:13185-13190)
function bcpNoteWithinThroughDate(note, throughDate) {
  if (!note) return null;
  const d = String(note.document_date || '').slice(0, 10);
  if (throughDate && d && d > throughDate) return null;
  return note;
}

// v2 source: bcpPriorCarePlanDate (ccm-compliance.js:13178-13184)
// _cpHistory in v2 is the in-memory cache of /data/careplans. In v3 we fetch
// once and re-shape into the same { pid: { month: text } } structure so this
// helper stays byte-identical to v2.
function bcpPriorCarePlanDate(cpHistory, patientId, currentMonthStr) {
  const h = (cpHistory && cpHistory[patientId]) || null;
  if (!h) return null;
  const months = Object.keys(h).filter(function(m) { return h[m] && m < currentMonthStr; }).sort();
  if (!months.length) return null;
  return months[months.length - 1] + '-01';
}

// v2 source: bcpBuildLabTrends (ccm-compliance.js:13191-13230)
// fetchLabReportsForPatient hits GET /data/labs/:patientId — the same URL the
// v2 SPA calls. The optional Elation-side enrichment for unlinked patients was
// inside fetchLabReportsForPatient on v2; replicating only the DB read keeps
// the lab-trend section equivalent for patients whose labs are already synced
// to the DB (the predominant case post-go-live).
// v2 source: fetchLabReportsForPatient (ccm-compliance.js:11368-11372)
async function fetchLabReportsForPatient(patientId) {
  if (!patientId) return [];
  try {
    const data = await api.get('/data/lab-reports/' + patientId);
    return Array.isArray(data) ? data : [];
  } catch (_) { return []; }
}

async function bcpBuildLabTrends(patientId, sinceDate, throughDate, monthStartDate) {
  const empty = { text: '', stale: false, allPreMonth: false };
  let reports = [];
  try { reports = await fetchLabReportsForPatient(patientId); } catch (e) { return empty; }
  if (!Array.isArray(reports) || !reports.length) return empty;
  function reportDate(r) {
    return r.report_date || (r.file_received || '').split(' ')[0] || '';
  }
  reports.sort(function(a, b) {
    const ad = reportDate(a);
    const bd = reportDate(b);
    return bd.localeCompare(ad);
  });
  const available = reports.filter(function(r) {
    const d = reportDate(r);
    if (!d) return false;
    if (throughDate && d > throughDate) return false;
    return true;
  });
  const inWindow = available.filter(function(r) {
    const d = reportDate(r);
    return !sinceDate || d >= sinceDate;
  });
  const stale = !!sinceDate && inWindow.length === 0;
  const picks = inWindow.length ? inWindow.slice(0, 5) : available.slice(0, 3);
  if (!picks.length) return { text: '', stale, allPreMonth: false };
  // Pre-period flag: true when every picked lab predates the first day of the
  // current reporting month. This catches the "no labs this billing month"
  // case (regardless of sinceDate) so downstream framing can mark the lab
  // block as historical and stop the AI from citing ancient values as
  // current measurements in the Problem List.
  const allPreMonth = !!monthStartDate && picks.every(function(r) {
    const d = reportDate(r);
    return d && d < monthStartDate;
  });
  const sections = picks.map(function(r) {
    const date = reportDate(r) || 'Unknown date';
    const resultLines = (r.results || []).map(function(x) {
      let line = '  - ' + (x.test_name || 'Unknown test') + ': ' + (x.value || 'N/A');
      if (x.flag) line += ' [' + x.flag + ']';
      if (x.reference_range) line += ' (ref: ' + x.reference_range + (x.units ? ' ' + x.units : '') + ')';
      else if (x.units) line += ' ' + x.units;
      return line;
    }).join('\n');
    return '- ' + date + ':\n' + resultLines;
  });
  return { text: sections.join('\n'), stale, allPreMonth };
}

// v2 source: reidentifyText (ccm-compliance.js:3623-3643) — re-insert [PATIENT_NAME]
// tokens with real names locally so PHI is never round-tripped through the AI.
const _KNOWN_SPECIALTY = /^(cardiology|psychiatry|ophthalmology|optometry|neurology|nephrology|urology|gastroenterology|hepatology|endocrinology|oncology|pulmonology|pulmonary|dermatology|hematology|rheumatology|orthopedics?|podiatry|gynecology|obstetrics|allergy|immunology|pain|palliative|primary|internal|family|pediatrics?|geriatrics?|surgery|otolaryngology|ent|sleep|wound|vascular|radiology|pathology|psychology|psychotherapy|counseling|nutrition|dietetics)$/i;
function reidentifyText(text, phiMap) {
  let result = text;
  Object.keys(phiMap).forEach(function(token) {
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(new RegExp(escaped, 'g'), phiMap[token]);
  });
  result = result.replace(/\[SPECIALIST_\d+\](\s+)(\w[\w-]*)/g, function(match, ws, next) {
    return _KNOWN_SPECIALTY.test(next) ? next : 'the specialist' + ws + next;
  });
  result = result.replace(/\[SPECIALIST_\d+\]/g, 'the specialist');
  return result;
}

// v2 source: normalizeAiModel (ccm-compliance.js:3409-3416)
function normalizeAiModel(model) {
  const raw = String(model || '').toLowerCase();
  if (raw === 'bedrock-opus' || raw === 'bedrock-sonnet' || raw === 'bedrock-haiku') return raw;
  if (raw.indexOf('opus') !== -1) return 'bedrock-opus';
  if (raw.indexOf('sonnet') !== -1) return 'bedrock-sonnet';
  if (raw.indexOf('haiku') !== -1 || raw.indexOf('mini') !== -1 || raw.indexOf('nano') !== -1) return 'bedrock-haiku';
  return 'bedrock-sonnet';
}

// v2 source: monthLastDayString (ccm-compliance.js:2168-2175)
function monthLastDayString(monthStr) {
  const isValid = /^\d{4}-(0[1-9]|1[0-2])$/.test(String(monthStr || ''));
  const raw = isValid ? monthStr : new Date().toISOString().slice(0, 7);
  const parts = raw.split('-');
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const day = new Date(y, m, 0).getDate();
  return raw + '-' + String(day).padStart(2, '0');
}

// v2 source: callAI (ccm-compliance.js:3525-3590)
async function callAI(prompt, model, options) {
  const opts = options || {};
  const modelId = normalizeAiModel(model || 'bedrock-sonnet');

  // v2 builds messages with multi-block content when opts.cachedPrefix is set.
  // The v3 bulk-care-plan path doesn't use the cached split, so we send the
  // single-string user content same as v2's bulk loop.
  let userContent;
  if (opts.cachedPrefix) {
    userContent = [
      { type: 'text', text: opts.cachedPrefix, cache_control: { type: 'ephemeral' } },
      { type: 'text', text: prompt },
    ];
  } else {
    userContent = prompt;
  }

  const payload = {
    model: modelId,
    max_tokens: opts.max_tokens || 4096,
    temperature: opts.temperature != null ? opts.temperature : 0.3,
    system: opts.system, // server adds the default system if absent
    systemCache: opts.systemCache === true,
    messages: [{ role: 'user', content: userContent }],
  };
  const data = await api.post('/ai', payload);
  if (!data.content || !data.content[0] || typeof data.content[0].text !== 'string') {
    throw new Error('AI response was missing expected text content.');
  }
  return data.content[0].text;
}

// Fetch existing care-plan history for this patient so the maintenance-mode
// shortcut (no new labs/notes since the prior plan) can fire correctly.
async function loadCarePlanHistoryFor(patientId) {
  try {
    const data = await ccmApi.listCarePlans();
    const cp = (data && data.care_plan) ? data.care_plan : (data || {});
    return { [patientId]: cp[patientId] || {} };
  } catch (_) {
    return { [patientId]: {} };
  }
}

// Resolve the configured AI model from /data/settings (same key v2 uses).
async function getConfiguredModel() {
  try {
    const s = await ccmApi.getSettings();
    if (s && s.aiModel) return normalizeAiModel(s.aiModel);
  } catch (_) {}
  return 'bedrock-sonnet';
}

// ── Public API ───────────────────────────────────────────────────────────────

// Computes the data-freshness signals that drive the care-plan prompt path
// (maintenance vs full) and the pre-generation 99487 review check, plus the
// current care-coordination context used to ground the generated plan.
// Composes bcpFetchLatestVisitNotes, bcpFetchPatientProviderTeams,
// bcpFetchPhoneNotesInMonth, loadCarePlanHistoryFor, bcpPriorCarePlanDate,
// bcpBuildLabTrends, and bcpNoteWithinThroughDate.
//
// `ctx` (optional) supplies pre-fetched shared data so bulk callers don't have
// to refetch ccmApi.listCarePlans()/latestVisitNotes() per patient:
//   - ctx.cpHistory:        same shape as loadCarePlanHistoryFor returns
//                           ({ [patientId]: { [yyyy-mm]: text } })
//   - ctx.latestNoteByEid:  same shape as bcpFetchLatestVisitNotes returns
//                           ({ [elationId]: { document_date, text } })
//   - ctx.providerTeamByEid: same shape as bcpFetchPatientProviderTeams returns
//                           ({ [elationId]: { teamMembers: [...] } })
//   - ctx.phoneNotesByEid:  same shape as bcpFetchPhoneNotesInMonth returns
//                           ({ [elationId]: [{ date, text }, ...] })
//
// Returns null when patientId or monthStr is missing. Otherwise:
//   { sinceDate, hasPriorPlan, labStale, noteStale, noNewData,
//     _labResult, _latestNote, _providerTeam, _phoneNotes,
//     _priorPlanText, _throughDate }
//
// The _-prefixed fields are passthrough for generateAndSaveCarePlan to skip
// re-fetching when the freshness was already computed upstream.
export async function getCarePlanFreshness(patient, monthStr, ctx) {
  const p = patient?._raw || patient || {};
  const patientId = patient?.id || p.id;
  if (!patientId || !monthStr) return null;
  const throughDate = monthLastDayString(monthStr);
  const monthStartDate = monthStr + '-01';
  const cpHistory = (ctx && ctx.cpHistory) || await loadCarePlanHistoryFor(patientId);
  const latestNoteByEid = (ctx && ctx.latestNoteByEid) || await bcpFetchLatestVisitNotes([p]);
  const providerTeamByEid = (ctx && ctx.providerTeamByEid) || await bcpFetchPatientProviderTeams([p]);
  const phoneNotesByEid = (ctx && ctx.phoneNotesByEid) || await bcpFetchPhoneNotesInMonth([p], monthStr);
  const sinceDate = bcpPriorCarePlanDate(cpHistory, patientId, monthStr);
  const labResult = await bcpBuildLabTrends(patientId, sinceDate, throughDate, monthStartDate);
  const rawLatestNote = p.elation_id ? bcpNoteWithinThroughDate(latestNoteByEid[String(p.elation_id)] || null, throughDate) : null;
  const latestNote = rawLatestNote || null;
  const providerTeam = p.elation_id ? (providerTeamByEid[String(p.elation_id)] || null) : null;
  const phoneNotes = p.elation_id ? (phoneNotesByEid[String(p.elation_id)] || []) : [];
  const noteStale = !!(sinceDate && (!latestNote || !latestNote.document_date || latestNote.document_date < sinceDate));
  const noNewData = !!sinceDate && labResult.stale && noteStale;
  const priorPlanText = (sinceDate && cpHistory[patientId])
    ? cpHistory[patientId][sinceDate.slice(0, 7)] || null
    : null;
  return {
    sinceDate,
    hasPriorPlan: !!sinceDate,
    labStale: labResult.stale,
    noteStale,
    noNewData,
    _labResult: labResult,
    _latestNote: latestNote,
    _providerTeam: providerTeam,
    _phoneNotes: phoneNotes,
    _priorPlanText: priorPlanText,
    _throughDate: throughDate,
    _monthStartDate: monthStartDate,
  };
}

// Mirrors the per-iteration body of v2's bcpGenerate (lines 14483-14540) for
// one patient. Returns { ok, planText, noNewData, error? }.
//
// opts.freshness — optional getCarePlanFreshness() result to reuse when the
// caller already computed it (e.g. the 99487 pre-generation review path).
// Avoids a duplicate listCarePlans / latestVisitNotes / lab-reports round.
export async function generateAndSaveCarePlan(patient, monthStr, opts) {
  const p = patient?._raw || patient || {};
  const patientId = patient?.id || p.id;
  if (!patientId) throw new Error('Patient id missing');
  if (!monthStr) throw new Error('Month missing');
  _pcmPlanGate(p);

  await loadTemplates();
  const model = await getConfiguredModel();

  const freshness = (opts && opts.freshness)
    ? opts.freshness
    : await getCarePlanFreshness(patient, monthStr);
  if (!freshness) throw new Error('Could not compute care-plan freshness');
  const { sinceDate, noNewData } = freshness;
  const labResult = freshness._labResult;
  const latestNote = freshness._latestNote;
  const providerTeam = freshness._providerTeam;
  const phoneNotes = freshness._phoneNotes;
  const priorPlanText = freshness._priorPlanText;

  const monthStartDate = freshness._monthStartDate || (monthStr + '-01');
  // Full (standard/complex) plans split into a cacheable template prefix + a
  // per-patient suffix so the static template head caches across a bulk run.
  // The maintenance path has a different (markerless) template, so it stays a
  // single non-cached string. cachedPrefix is '' when caching doesn't apply.
  let cachedPrefix = '';
  let basePrompt;
  if (shouldUseMaintenancePrompt(p, noNewData, priorPlanText)) {
    basePrompt = buildMaintenanceCarePlanPrompt(p, priorPlanText, sinceDate, {
      monthStr,
      providerTeam,
      phoneNotes,
    });
  } else {
    const split = buildFullCarePlanPromptCached(p, {
      labTrends: labResult.text,
      labStale: labResult.stale,
      labAllPreMonth: labResult.allPreMonth,
      monthStartDate,
      monthStr,
      latestNote,
      providerTeam,
      phoneNotes,
      sinceDate,
      priorPlanText,
      priorPlanMonth: sinceDate ? sinceDate.slice(0, 7) : null,
    });
    cachedPrefix = split.cachedPrefix;
    basePrompt = split.dynamicSuffix;
  }

  // Program-aware BHI extension: when the patient is BHI-enrolled, append a
  // <bhi_section> block that instructs the AI to add a Behavioral Health Care
  // Planning section referencing the most-recent assessment and BH dx. No-op
  // for CCM-only patients. See careplan-bhi.js for the section template and
  // the known limitation note about BHI-only enrollments. The section is
  // patient-specific, so it rides in the dynamic suffix (never the cached prefix).
  // PCM plans never take the combined-BHI section (codex review 2026-07-20):
  // careplan-bhi frames the output as a comprehensive plan covering chronic
  // care AND behavioral health, contradicting the single-condition PCM scope.
  // A PCM+BHI patient's behavioral documentation lives in the BHI billing
  // note/assessments, not in the disease-specific PCM plan. The pain section
  // (P2.1) shares the skip: same single-condition-scope reasoning, and pain
  // billing never pairs with PCM at all (pain-billing pairing rule: CCM tiers
  // + APCM only), so a pain section inside a PCM plan could only describe
  // non-billable context. Every non-PCM route (standard/maintenance/complex)
  // takes both sections.
  const prompt = basePrompt + (isPcmProgramCode(p.billing && p.billing.program) ? '' : buildBhiPromptSection(p, monthStr) + buildPainPromptSection(p, monthStr));

  // Care plans run long — full 99490/99487 narrative across all CMS-required
  // sections regularly exceeds 4096 output tokens, causing mid-sentence cutoff
  // in the "Review and Revision Schedule" footer. 8192 is the safe ceiling
  // across every Bedrock model alias on the allowlist (Haiku 4.5 maxes at
  // 8192; Sonnet/Opus support more if a future flow opts in).
  let aiText = await callAI(prompt, model, { max_tokens: 8192, cachedPrefix: cachedPrefix || undefined });
  if (!aiText || !aiText.trim()) throw new Error('Empty response');

  // Re-identify [PATIENT_NAME] tokens locally — never sent back to server.
  const phiMap = { '[PATIENT_NAME]': ((p.fname || '') + ' ' + (p.lname || '')).trim() || 'patient' };
  aiText = reidentifyText(aiText, phiMap);

  // Save — same endpoint + body shape as v2 (saveCarePlan and bulk loop both
  // call dbPost('/data/careplans/:pid/:month', { plan_text }) — type omitted
  // so the server defaults to 'care_plan').
  await ccmApi.saveCarePlan(patientId, monthStr, aiText);

  // Force the next useDraft() call to refetch so the new plan shows up.
  invalidateDraftCache();

  return { ok: true, planText: aiText, noNewData };
}

// MIRROR of generateAndSaveCarePlan's prompt-build (~:605-661); keep in sync — Task 4b drift-guard enforces builder-call parity.
// Builds the SAME prompt but returns a batch record instead of calling callAI.
// If you change the prompt-build sequence above, MIRROR it here. The Task-4b
// drift-guard test fails if this function stops referencing a builder that
// generateAndSaveCarePlan uses.
export async function buildCarePlanRecord(patient, monthStr, opts) {
  const p = patient?._raw || patient || {};
  const patientId = patient?.id || p.id;
  if (!patientId) throw new Error('Patient id missing');
  if (!monthStr) throw new Error('Month missing');
  _pcmPlanGate(p);

  await loadTemplates();
  const model = await getConfiguredModel();

  const freshness = (opts && opts.freshness)
    ? opts.freshness
    : await getCarePlanFreshness(patient, monthStr);
  if (!freshness) throw new Error('Could not compute care-plan freshness');
  const { sinceDate, noNewData } = freshness;
  const labResult = freshness._labResult;
  const latestNote = freshness._latestNote;
  const providerTeam = freshness._providerTeam;
  const phoneNotes = freshness._phoneNotes;
  const priorPlanText = freshness._priorPlanText;

  const monthStartDate = freshness._monthStartDate || (monthStr + '-01');
  // Full (standard/complex) plans split into a cacheable template prefix + a
  // per-patient suffix so the static template head caches across a bulk run.
  // The maintenance path has a different (markerless) template, so it stays a
  // single non-cached string. cachedPrefix is '' when caching doesn't apply.
  let cachedPrefix = '';
  let basePrompt;
  if (shouldUseMaintenancePrompt(p, noNewData, priorPlanText)) {
    basePrompt = buildMaintenanceCarePlanPrompt(p, priorPlanText, sinceDate, {
      monthStr,
      providerTeam,
      phoneNotes,
    });
  } else {
    const split = buildFullCarePlanPromptCached(p, {
      labTrends: labResult.text,
      labStale: labResult.stale,
      labAllPreMonth: labResult.allPreMonth,
      monthStartDate,
      monthStr,
      latestNote,
      providerTeam,
      phoneNotes,
      sinceDate,
      priorPlanText,
      priorPlanMonth: sinceDate ? sinceDate.slice(0, 7) : null,
    });
    cachedPrefix = split.cachedPrefix;
    basePrompt = split.dynamicSuffix;
  }

  // Program-aware BHI extension: when the patient is BHI-enrolled, append a
  // <bhi_section> block that instructs the AI to add a Behavioral Health Care
  // Planning section referencing the most-recent assessment and BH dx. No-op
  // for CCM-only patients. See careplan-bhi.js for the section template and
  // the known limitation note about BHI-only enrollments. The section is
  // patient-specific, so it rides in the dynamic suffix (never the cached prefix).
  // PCM plans never take the combined-BHI section (codex review 2026-07-20):
  // careplan-bhi frames the output as a comprehensive plan covering chronic
  // care AND behavioral health, contradicting the single-condition PCM scope.
  // A PCM+BHI patient's behavioral documentation lives in the BHI billing
  // note/assessments, not in the disease-specific PCM plan. The pain section
  // (P2.1) shares the skip: same single-condition-scope reasoning, and pain
  // billing never pairs with PCM at all (pain-billing pairing rule: CCM tiers
  // + APCM only), so a pain section inside a PCM plan could only describe
  // non-billable context. Every non-PCM route (standard/maintenance/complex)
  // takes both sections.
  const prompt = basePrompt + (isPcmProgramCode(p.billing && p.billing.program) ? '' : buildBhiPromptSection(p, monthStr) + buildPainPromptSection(p, monthStr));

  // Same phiMap the realtime path builds for reidentify — so batch reidentify
  // produces a byte-identical note. max_tokens/temperature match the realtime
  // callAI args (8192 / 0.3); the batch run model is resolved server-side at submit.
  const phiMap = { '[PATIENT_NAME]': ((p.fname || '') + ' ' + (p.lname || '')).trim() || 'patient' };
  return {
    type: 'care_plan',
    model,
    promptBody: { max_tokens: 8192, temperature: 0.3, messages: buildUserMessages(prompt, cachedPrefix || undefined) },
    assemblerCtx: { phiMap },
  };
}

// Materialize a batch care-plan result into the exact note the realtime path
// would have saved, then persist it. Used ONLY by the batch orchestrator
// (realtime keeps its own inline reidentify + save in generateAndSaveCarePlan).
export async function materializeCarePlanResult(patient, monthStr, rawText) {
  const p = patient?._raw || patient || {};
  const patientId = patient?.id || p.id;
  const phiMap = { '[PATIENT_NAME]': ((p.fname || '') + ' ' + (p.lname || '')).trim() || 'patient' };
  const note = buildMaterializedNote('care_plan', rawText, { phiMap }, {});
  await ccmApi.saveCarePlan(patientId, monthStr, note);
  invalidateDraftCache();
  return note;
}
